/**
 * Дерево подразделений для выпадающих списков (TreeSelect).
 *
 * Подразделения офиса образуют дерево (Department.parentId). Во всех формах
 * и фильтрах выбор идёт из дерева, чтобы было видно, что «Колонна» входит в
 * «Департамент», а не стоит с ним на одном уровне.
 */
export interface DepartmentLike {
  id: number;
  name: string;
  parentId?: number | null;
}

export interface DepartmentTreeNode {
  value: number;
  title: string;
  children?: DepartmentTreeNode[];
}

/** Подразделение и все вложенные в него. */
export function departmentSubtreeIds(departments: DepartmentLike[], rootId: number): Set<number> {
  const result = new Set<number>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (result.has(id)) continue;
    result.add(id);
    for (const d of departments) {
      if (d.parentId === id) stack.push(d.id);
    }
  }
  return result;
}

/**
 * Узлы для TreeSelect. `exclude` — исключить поддерево (при выборе
 * вышестоящего подразделения нельзя предлагать само подразделение и его
 * вложенные: получился бы цикл).
 */
export function departmentTreeData(
  departments: DepartmentLike[],
  exclude?: number,
): DepartmentTreeNode[] {
  const hidden = exclude !== undefined ? departmentSubtreeIds(departments, exclude) : new Set<number>();
  const visible = departments.filter((d) => !hidden.has(d.id));
  const ids = new Set(visible.map((d) => d.id));

  const build = (parentId: number | null): DepartmentTreeNode[] =>
    visible
      .filter((d) =>
        parentId === null
          ? // Верхний уровень — и те, чей родитель скрыт или неизвестен.
            d.parentId == null || !ids.has(d.parentId)
          : d.parentId === parentId,
      )
      .map((d) => {
        const children = build(d.id);
        return { value: d.id, title: d.name, ...(children.length > 0 && { children }) };
      });

  return build(null);
}
